import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import backendUrl from "../../BackendUrl";

// Helper to get the right image
function getCarImage({ selectedClass, selectedModel, selectedTrim, selectedColor }) {
  if (selectedColor && selectedColor.colorImg) return selectedColor.colorImg;
  if (selectedTrim && selectedTrim.trimImg) return selectedTrim.trimImg;
  if (selectedModel && selectedModel.modelImg) return selectedModel.modelImg;
  if (selectedClass && selectedClass.classImg) return selectedClass.classImg;
  return "default-car-image-url";
}

// Define max values for each bar spec
const BAR_SPEC_MAX = {
  "Estimated MPG": 37,
  "Mileage": 510,
  "Weight Capacity": 3300,
  "Seat Count": 9,
};

// Helper to render spec bars and overlays
function SpecBars({ specs, overlaySpecs }) {
  return (
    <div className="spec-bars">
      {specs.map((s) => {
        const overlay = overlaySpecs?.find((o) => o.spec === s.spec);
        const maxValue = BAR_SPEC_MAX[s.spec] || 1;
        const percent = (s.value / maxValue) * 100;
        const overlayPercent = overlay ? (overlay.delta / maxValue) * 100 : 0;
        return (
          <div key={s.spec} className="spec-bar-row">
            <div className="spec-label">{s.spec}</div>
            <div className="spec-bar-bg" style={{ position: "relative" }}>
              <div
                className="spec-bar-value-label"
                style={{
                  left: `calc(${percent}% - 18px)`,
                }}
              >
                {s.value}
              </div>
              <div className="spec-bar-main" style={{ width: `${percent}%` }} />
              {overlay && (
                <div
                  className={`spec-bar-overlay ${overlay.delta > 0 ? "increase" : "decrease"}`}
                  style={{
                    width: `${Math.abs(overlayPercent)}%`,
                    left: overlay.delta > 0 ? `${percent}%` : `${percent + overlayPercent}%`,
                  }}
                />
              )}
            </div>
            <div className="spec-value">
              {s.value}
              {overlay && (
                <span style={{ color: overlay.delta > 0 ? "green" : "red", marginLeft: 4 }}>
                  {overlay.delta > 0 ? "+" : ""}
                  {overlay.delta}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Helper: get the delta for each spec from a list of accessories/packages
function getOverlaySpecs({ selectedAccessories, selectedPackage, availableAccessories }) {
  const deltas = {};
  function addDeltas(obj) {
    if (!obj) return;
    if (typeof obj.mpgCmb === "number") deltas["Estimated MPG"] = (deltas["Estimated MPG"] || 0) + obj.mpgCmb;
    if (typeof obj.mileage === "number") deltas["Mileage"] = (deltas["Mileage"] || 0) + obj.mileage;
    if (typeof obj.weight === "number") deltas["Weight Capacity"] = (deltas["Weight Capacity"] || 0) + obj.weight;
    if (typeof obj.seats === "number") deltas["Seat Count"] = (deltas["Seat Count"] || 0) + obj.seats;
  }
  // Add selected accessories (by _id)
  if (selectedAccessories && Array.isArray(selectedAccessories)) {
    selectedAccessories.forEach((acc) => addDeltas(acc));
  }
  // Add package's own deltas (if any)
  addDeltas(selectedPackage);
  return Object.entries(deltas).map(([spec, delta]) => ({ spec, delta }));
}

const CenterPanel = ({
  selectedClass,
  selectedModel,
  selectedTrim,
  selectedColor,
  selectedAccessories = [],
  selectedPackage,
  selectedOffer,
  availableAccessories = [],
  hoveredModel,
  hoveredTrim,
}) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [buildNamePrompt, setBuildNamePrompt] = useState(false);
  const [buildNameInput, setBuildNameInput] = useState("");
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [trims, setTrims] = useState([]);
  const [hoveredModelTrims, setHoveredModelTrims] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    setIsSignedIn(!!token);
  }, []);

  // Fetch trims for the selected model to calculate averages
  useEffect(() => {
    if (selectedModel) {
      axios
        .get(`${backendUrl}/api/vehicles/get-trim`, {
          params: { vehicleModelId: selectedModel._id },
        })
        .then((res) => {
          const filteredTrims = res.data.filter(
            (trim) =>
              trim.vehicleModelId &&
              trim.vehicleModelId.toString() === selectedModel._id.toString() &&
              trim.ready
          );
          setTrims(filteredTrims);
        })
        .catch((err) => {
          console.error("Error fetching trims:", err);
          setTrims([]);
        });
    } else {
      setTrims([]);
    }
  }, [selectedModel]);

  // Fetch trims for hovered model
  useEffect(() => {
    if (hoveredModel && hoveredModel._id !== selectedModel?._id) {
      axios
        .get(`${backendUrl}/api/vehicles/get-trim`, {
          params: { vehicleModelId: hoveredModel._id },
        })
        .then((res) => {
          const filteredTrims = res.data.filter(
            (trim) =>
              trim.vehicleModelId &&
              trim.vehicleModelId.toString() === hoveredModel._id.toString() &&
              trim.ready
          );
          setHoveredModelTrims(filteredTrims);
        })
        .catch((err) => {
          console.error("Error fetching hovered model trims:", err);
          setHoveredModelTrims([]);
        });
    } else {
      setHoveredModelTrims([]);
    }
  }, [hoveredModel, selectedModel]);

  const handleSaveVehicle = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSaveError("You must be signed in to save vehicles.");
      return;
    }

    if (!buildNameInput.trim()) return;

    const vehicleData = {
      buildName: buildNameInput,
      class: selectedClass.class,
      model: selectedModel.model,
      modelId: selectedModel._id,
      trim: selectedTrim.trim,
      trimPrice: selectedTrim.price,
      color: selectedColor.color,

      // Package info + deltas
      package: selectedPackage?.package,
      packageDesc: selectedPackage?.description,
      packageAccessories: selectedPackage?.accessories || [],
      packagePrice: selectedPackage?.price || 0,
      packageMpgCity: selectedPackage?.mpgCity || 0,
      packageMpgHwy: selectedPackage?.mpgHwy || 0,
      packageMpgCmb: selectedPackage?.mpgCmb || 0,
      packageMileage: selectedPackage?.mileage || 0,
      packageWeight: selectedPackage?.weight || 0,
      packageSeats: selectedPackage?.seats || 0,

      // Accessories + deltas
      accessories: selectedAccessories.map((acc) => ({
        accessory: acc.accessory,
        accessoryDesc: acc.description,
        accessoryPrice: acc.price,
        mpgCity: acc.mpgCity || 0,
        mpgHwy: acc.mpgHwy || 0,
        mpgCmb: acc.mpgCmb || 0,
        mileage: acc.mileage || 0,
        weight: acc.weight || 0,
        seats: acc.seats || 0,
      })),
      accessoriesPrice,

      // Base trim specs (to be summed with above)
      mpgCity: selectedTrim?.mpgCity || 0,
      mpgHwy: selectedTrim?.mpgHwy || 0,
      mpgCmb: selectedTrim?.mpgCmb || 0,
      mileage: selectedTrim?.mileage || 0,
      weight: selectedTrim?.weight || 0,
      seats: selectedTrim?.seats || 0,

      engine: selectedTrim?.engine,
      transmission: selectedTrim?.transmission,

      offer: selectedOffer?.offer,
      offerId: selectedOffer?._id,
      offerDesc: selectedOffer?.description,
      percent: selectedOffer?.percent,
      totalPrice,
      percentPrice: subtotal - totalPrice,

      savedImg: selectedColor?.colorImg
    };

    try {
      setSaving(true);
      setSaveError(null);

      await axios.post("/api/accounts/saveVehicle", vehicleData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setBuildNamePrompt(false);
      setBuildNameInput("");
      navigate("/profile"); // 👈 Send user to their profile page
    } catch (err) {
      setSaveError(err.response?.data?.message || "Failed to save vehicle.");
    } finally {
      setSaving(false);
    }
  };

  // 1. Prompt if no class selected
  if (!selectedClass) {
    return (
      <main className="center-panel">
        <div className="center-panel-prompt">
          <h2>Please choose a class to begin.</h2>
        </div>
      </main>
    );
  }

  // 2. Car image
  const carImage = getCarImage({ selectedClass, selectedModel, selectedTrim, selectedColor });

  // 3. Prepare bar specs from trim fields if trim is selected
  let barSpecs = [];
  let textSpecs = [];
  let description = "";
  let overlaySpecs = [];
  let price = 0;
  let accessoriesPrice = 0;
  let packagePrice = 0;

  if (selectedTrim) {
    description = selectedTrim.description;
    price = selectedTrim.price || 0;

    // Build bar specs from trim fields
    barSpecs = [
      { spec: "Estimated MPG", value: Number(selectedTrim.mpgCmb) || 0 },
      { spec: "Mileage", value: Number(selectedTrim.mileage) || 0 },
      { spec: "Weight Capacity", value: Number(selectedTrim.weight) || 0 },
      { spec: "Seat Count", value: Number(selectedTrim.seats) || 0 },
    ];

    // Text specs
    textSpecs = [
      { label: "Engine", value: selectedTrim.engine },
      { label: "Transmission", value: selectedTrim.transmission },
      { label: "MPG City", value: selectedTrim.mpgCity },
      { label: "MPG Highway", value: selectedTrim.mpgHwy },
    ].filter((s) => s.value !== undefined && s.value !== "");

    // Overlay for accessories/packages or hover comparison
    if (hoveredTrim) {
      // Compare selectedTrim with hoveredTrim
      overlaySpecs = [
        { spec: "Estimated MPG", delta: (Number(hoveredTrim.mpgCmb) || 0) - (Number(selectedTrim.mpgCmb) || 0) },
        { spec: "Mileage", delta: (Number(hoveredTrim.mileage) || 0) - (Number(selectedTrim.mileage) || 0) },
        { spec: "Weight Capacity", delta: (Number(hoveredTrim.weight) || 0) - (Number(selectedTrim.weight) || 0) },
        { spec: "Seat Count", delta: (Number(hoveredTrim.seats) || 0) - (Number(selectedTrim.seats) || 0) },
      ];
    } else {
      overlaySpecs = getOverlaySpecs({ selectedAccessories, selectedPackage, availableAccessories });
    }

    // Prices
    if (selectedPackage) packagePrice = selectedPackage.price || 0;
    if (selectedAccessories.length > 0) {
      selectedAccessories.forEach((acc) => {
        accessoriesPrice += acc.price || 0;
      });
    }
  } else if (selectedModel) {
    description = selectedModel.description;

    // Calculate average specs from trims
    if (trims.length > 0) {
      const avgMpgCmb = trims.reduce((sum, t) => sum + (Number(t.mpgCmb) || 0), 0) / trims.length;
      const avgMileage = trims.reduce((sum, t) => sum + (Number(t.mileage) || 0), 0) / trims.length;
      const avgWeight = trims.reduce((sum, t) => sum + (Number(t.weight) || 0), 0) / trims.length;
      const avgSeats = trims.reduce((sum, t) => sum + (Number(t.seats) || 0), 0) / trims.length;

      barSpecs = [
        { spec: "Estimated MPG", value: Math.round(avgMpgCmb) },
        { spec: "Mileage", value: Math.round(avgMileage) },
        { spec: "Weight Capacity", value: Math.round(avgWeight) },
        { spec: "Seat Count", value: Math.round(avgSeats) },
      ];

      // Overlay for hover comparison with another model
      if (hoveredModel && hoveredModel._id !== selectedModel._id && hoveredModelTrims.length > 0) {
        const hoveredAvgMpgCmb = hoveredModelTrims.reduce((sum, t) => sum + (Number(t.mpgCmb) || 0), 0) / hoveredModelTrims.length;
        const hoveredAvgMileage = hoveredModelTrims.reduce((sum, t) => sum + (Number(t.mileage) || 0), 0) / hoveredModelTrims.length;
        const hoveredAvgWeight = hoveredModelTrims.reduce((sum, t) => sum + (Number(t.weight) || 0), 0) / hoveredModelTrims.length;
        const hoveredAvgSeats = hoveredModelTrims.reduce((sum, t) => sum + (Number(t.seats) || 0), 0) / hoveredModelTrims.length;

        const selectedAvgMpgCmb = barSpecs.find(s => s.spec === "Estimated MPG").value;
        const selectedAvgMileage = barSpecs.find(s => s.spec === "Mileage").value;
        const selectedAvgWeight = barSpecs.find(s => s.spec === "Weight Capacity").value;
        const selectedAvgSeats = barSpecs.find(s => s.spec === "Seat Count").value;

        overlaySpecs = [
          { spec: "Estimated MPG", delta: Math.round(hoveredAvgMpgCmb) - selectedAvgMpgCmb },
          { spec: "Mileage", delta: Math.round(hoveredAvgMileage) - selectedAvgMileage },
          { spec: "Weight Capacity", delta: Math.round(hoveredAvgWeight) - selectedAvgWeight },
          { spec: "Seat Count", delta: Math.round(hoveredAvgSeats) - selectedAvgSeats },
        ];
      }
    }
  } else if (selectedClass) {
    description = selectedClass.description;
  }

  // Accessories/Packages display
  const selectedAccessoryNames = (selectedAccessories || []).map((acc) => acc.accessory).join(", ");
  const selectedPackageName = selectedPackage ? selectedPackage.package : "";

  // Total price
  const subtotal = price + accessoriesPrice + packagePrice;
  const discountAmount = selectedOffer
    ? (subtotal * (selectedOffer.percent || 0)) / 100
    : 0;
  const totalPrice = subtotal - discountAmount;

  // Title logic
  let title = "";
  if (selectedModel) {
    if (selectedTrim) {
      if (selectedColor) {
        title = `${selectedColor.color} | ${selectedModel.model} - ${selectedTrim.trim}`;
      } else {
        title = `${selectedModel.model} - ${selectedTrim.trim}`;
      }
    } else {
      if (selectedColor) {
        title = `${selectedColor.color} | ${selectedModel.model}`;
      } else {
        title = selectedModel.model;
      }
    }
  }

  return (
    <main className="center-panel">
      {/* Title above image */}
      {title && (
        <div className="center-panel-title">
          {title}
        </div>
      )}
      {/* Car image */}
      <div className="car-image">
        <img src={carImage} alt="Car" />
      </div>
      {/* Two-column or single-column content */}
      {(selectedTrim || (selectedModel && barSpecs.length > 0)) ? (
        <div className="center-panel-content">
          {/* Left column */}
          <div className="center-panel-left">
            {/* Text Specs - only for trim */}
            {selectedTrim && textSpecs.length > 0 && (
              <div className="text-specs">
                {textSpecs.map((s) => (
                  <div key={s.label} className="text-spec-row">
                    <strong>{s.label}:</strong> {s.value}
                  </div>
                ))}
              </div>
            )}

            {/* Accessories/Packages - only for trim */}
            {selectedTrim && (selectedAccessoryNames || selectedPackageName) && (
              <div className="selected-accessories">
                {selectedPackageName && (
                  <div>
                    <strong>Package:</strong> {selectedPackageName}
                  </div>
                )}
                {selectedAccessoryNames && (
                  <div>
                    <strong>Accessories:</strong> {selectedAccessoryNames}
                  </div>
                )}
              </div>
            )}

            {/* Included Accessories - only for trim */}
            {selectedTrim && selectedPackage?.accessories?.length > 0 && (
              <div className="included-accessories">
                <strong>Included Accessories:</strong>
                <ul>
                  {selectedPackage.accessories.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Offer - only for trim */}
            {selectedTrim && selectedOffer && (
              <div>
                <strong>Offer:</strong> {selectedOffer.offer}
              </div>
            )}

            {/* Description for model */}
            {selectedModel && !selectedTrim && (
              <div className="center-panel-description">
                <p>{description}</p>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="center-panel-right">
            <SpecBars specs={barSpecs} overlaySpecs={overlaySpecs} />
            {selectedTrim && (
              <div className="pricing-summary">
                <div>
                  <strong>Trim Price:</strong> ${price.toLocaleString()}
                </div>
                {packagePrice > 0 && (
                  <div>
                    <strong>Package Price:</strong> ${packagePrice.toLocaleString()}
                  </div>
                )}
                {accessoriesPrice > 0 && (
                  <div>
                    <strong>Accessories Price:</strong> ${accessoriesPrice.toLocaleString()}
                  </div>
                )}
                <div>
                  <strong>Subtotal:</strong> ${subtotal.toLocaleString()}
                </div>
                {selectedOffer && (
                  <div>
                    <strong>Offer Discount:</strong> -${discountAmount.toLocaleString()}
                  </div>
                )}
                <div>
                  <strong>Total Price:</strong> ${totalPrice.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="center-panel-description">
          <p>{description}</p>
        </div>
      )}
      {/* Save Vehicle Section */}
      <div className="save-vehicle-section">
        {!isSignedIn ? (
          <div className="save-btn-disabled">
            <button className="save-btn disabled" disabled>
              Save This Vehicle
            </button>
            <p className="save-requirements-msg">
              Please <a href="/signin">sign in</a> to save your vehicle.
            </p>
          </div>
        ) : selectedClass && selectedModel && selectedTrim && selectedColor ? (
          buildNamePrompt ? (
            <div className="build-name-prompt">
              <input
                type="text"
                placeholder="Enter build name"
                value={buildNameInput}
                onChange={(e) => setBuildNameInput(e.target.value)}
              />
              <button onClick={handleSaveVehicle} disabled={saving || !buildNameInput}>
                {saving ? "Saving..." : "Confirm Save"}
              </button>
              <button onClick={() => setBuildNamePrompt(false)}>Cancel</button>
              {saveError && <p className="error">{saveError}</p>}
            </div>
          ) : (
            <button className="save-btn" onClick={() => setBuildNamePrompt(true)}>
              Save This Vehicle
            </button>
          )
        ) : (
          <div className="save-btn-disabled">
            <button className="save-btn disabled" disabled>
              Save This Vehicle
            </button>
            <p className="save-requirements-msg">
              Please select a class, model, trim, and color to save your vehicle.
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default CenterPanel;